// Copyright 2025, University of Maryland, All Rights Reserved
import type { NodeApiClient } from '@/app-shell/services'
import type { Agent, AgentKnowledgeBase, AgentTemplate, CreateAgentPayload, InstantiateAgentPayload, PromoteAgentPayload, PromoteAgentPreview, UpdateAgentPayload } from './types'

export async function listAgents(
  client: NodeApiClient,
  orgId: string,
  wsId: string,
): Promise<Agent[]> {
  const { data } = await client.get<Agent[]>(`/workspaces/${orgId}/${wsId}/agents`)
  return data
}

export async function createAgent(
  client: NodeApiClient,
  orgId: string,
  wsId: string,
  payload: CreateAgentPayload,
): Promise<Agent> {
  const { data } = await client.post<Agent>(`/workspaces/${orgId}/${wsId}/agents`, payload)
  return data
}

export async function updateAgent(
  client: NodeApiClient,
  orgId: string,
  wsId: string,
  agentId: string,
  payload: UpdateAgentPayload,
): Promise<Agent> {
  const { data } = await client.patch<Agent>(`/workspaces/${orgId}/${wsId}/agents/${agentId}`, payload)
  return data
}

export async function listAgentTemplates(
  client: NodeApiClient,
  orgId: string,
  wsId: string,
): Promise<AgentTemplate[]> {
  const { data } = await client.get<AgentTemplate[]>(`/workspaces/${orgId}/${wsId}/agent-templates`)
  return data
}

export async function instantiateAgent(
  client: NodeApiClient,
  orgId: string,
  wsId: string,
  payload: InstantiateAgentPayload,
): Promise<Agent> {
  const { data } = await client.post<Agent>(`/workspaces/${orgId}/${wsId}/agents/from-template`, payload)
  return data
}

export async function promoteAgent(
  client: NodeApiClient,
  orgId: string,
  wsId: string,
  agentId: string,
  payload: PromoteAgentPayload,
): Promise<Agent> {
  const { data } = await client.post<Agent>(
    `/workspaces/${orgId}/${wsId}/agents/${agentId}/promote`,
    payload,
  )
  return data
}

export async function getPromotePreview(
  client: NodeApiClient,
  orgId: string,
  wsId: string,
  agentId: string,
): Promise<PromoteAgentPreview> {
  const { data } = await client.get<PromoteAgentPreview>(
    `/workspaces/${orgId}/${wsId}/agents/${agentId}/promote-preview`,
  )
  return data
}

export async function deleteAgent(
  client: NodeApiClient,
  orgId: string,
  wsId: string,
  agentId: string,
  deleteKnowledgeBases = false,
): Promise<void> {
  await client.delete(
    `/workspaces/${orgId}/${wsId}/agents/${agentId}${deleteKnowledgeBases ? '?delete_knowledge_bases=true' : ''}`,
  )
}

export async function listAgentKnowledgeBases(
  client: NodeApiClient,
  orgId: string,
  wsId: string,
  agentId: string,
): Promise<AgentKnowledgeBase[]> {
  const { data } = await client.get<AgentKnowledgeBase[]>(
    `/workspaces/${orgId}/${wsId}/agents/${agentId}/knowledge_bases`,
  )
  return data
}
